'use client';

import Link from 'next/link';
import { motion } from 'framer-motion';
import Logo from '@/components/icons/Logo';
import { CornerDownLeft, Search, Atom, Database, Radio, Cpu } from 'lucide-react';

const FloatingIcon = ({ icon: Icon, delay, initialX, initialY }: any) => (
  <motion.div
    initial={{ x: initialX, y: initialY, opacity: 0 }}
    animate={{ 
      y: [initialY, initialY - 40, initialY],
      opacity: [0.1, 0.4, 0.1],
    }}
    transition={{ 
      duration: 6, 
      repeat: Infinity, 
      delay,
      ease: "easeInOut"
    }}
    className="absolute text-brand-dark/20 dark:text-brand-dark-text-primary/10 hidden md:block"
  >
    <Icon size={120} strokeWidth={0.5} />
  </motion.div>
);

export default function RootNotFound() {
  return (
    <div className='bg-background relative flex min-h-screen flex-col items-center justify-center overflow-hidden px-4 text-center transition-colors duration-500'>
      {/* Premium Background layers */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(6,102,83,0.05),transparent_50%)]" />
        <div className="absolute top-0 left-0 h-full w-full bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-[0.03] dark:opacity-[0.05]" />
      </div>

      {/* Floating Elements */}
      <FloatingIcon icon={Atom} delay={0} initialX={-400} initialY={-200} />
      <FloatingIcon icon={Database} delay={2} initialX={450} initialY={150} />
      <FloatingIcon icon={Radio} delay={4} initialX={-350} initialY={250} />
      <FloatingIcon icon={Cpu} delay={1} initialX={380} initialY={-250} />

      {/* Main Content Card */}
      <motion.div 
        initial={{ opacity: 0, y: 30, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className='relative z-10 w-full max-w-2xl px-6'
      >
        <div className='relative mx-auto mb-12 flex flex-col items-center'>
          {/* Logo with pulsating glow */}
          <motion.div 
            animate={{ scale: [1, 1.05, 1], boxShadow: ["0 0 20px rgba(6,102,83,0)", "0 0 40px rgba(6,102,83,0.3)", "0 0 20px rgba(6,102,83,0)"] }}
            transition={{ duration: 4, repeat: Infinity }}
            className='bg-card border-border/40 relative mb-8 rounded-[2rem] border p-8 shadow-2xl backdrop-blur-3xl dark:bg-slate-900/40'
          >
            <Logo className='text-brand-dark dark:text-brand-dark-text-primary size-20' />
          </motion.div>

          <div className='relative'>
            <span className='from-brand-dark/5 to-brand-dark/10 dark:from-white/5 dark:to-white/10 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-gradient-to-b bg-clip-text text-[15rem] leading-none font-black text-transparent select-none md:text-[20rem]'>
              404
            </span>
            <h1 className='text-foreground relative z-10 font-serif text-5xl font-black tracking-tight drop-shadow-2xl md:text-7xl'>
              Data <span className='bg-brand-dark dark:bg-brand-dark-text-primary bg-clip-text text-transparent'>Sequencing</span> Error
            </h1>
          </div>
        </div>

        <p className='text-muted-foreground/90 mx-auto mt-6 max-w-lg text-lg leading-relaxed font-medium md:text-xl'>
          Our systems encountered a gap in the protocol. The target coordinates you provided don&apos;t match any active research repository in our database.
        </p>

        {/* Dynamic Action Buttons */}
        <div className='mt-12 flex flex-col items-center justify-center gap-6 sm:flex-row'>
          <Link
            href='/'
            className='group bg-brand-dark hover:bg-brand-dark/95 dark:bg-brand-dark-text-primary dark:text-brand-dark relative flex items-center gap-3 overflow-hidden rounded-2xl px-12 py-5 text-sm font-black tracking-widest text-white uppercase transition-all hover:-translate-y-1 hover:shadow-[0_20px_40px_-15px_rgba(6,102,83,0.4)]'
          >
            <CornerDownLeft className='size-5 transition-transform group-hover:-translate-x-2' />
            Reset Protocol
          </Link>

          <Link
            href='/contact'
            className='border-border/60 bg-background/50 text-foreground hover:bg-muted/50 hover:border-brand-dark group/btn flex items-center gap-3 rounded-2xl border-2 px-12 py-5 text-sm font-black tracking-widest uppercase backdrop-blur-md transition-all'
          >
            <Search className='size-5 transition-transform group-hover/btn:scale-110' />
            Support Hub
          </Link>
        </div>

        {/* System Diagnostics footer */}
        <div className='mt-24 flex flex-col items-center gap-6 opacity-40 grayscale transition-all hover:opacity-100 hover:grayscale-0'>
          <div className='h-[1px] w-24 bg-gradient-to-r from-transparent via-brand-dark to-transparent' />
          <div className='text-muted-foreground flex flex-wrap justify-center gap-x-8 gap-y-4 text-[10px] font-black tracking-[0.3em] uppercase'>
            <div className='flex items-center gap-2'>
              <div className='size-1.5 animate-pulse rounded-full bg-red-500' />
              System: OFFLINE
            </div>
            <div className='flex items-center gap-2'>
              <div className='size-1.5 rounded-full bg-brand-dark opacity-50' />
              Code: DATA_GAP_404
            </div>
            <div className='hidden items-center gap-2 md:flex'>
              <div className='size-1.5 rounded-full bg-brand-dark opacity-50' />
              Sector: VOID_Z_0
            </div>
          </div>
        </div>
      </motion.div>

      {/* Grid Pattern overlay */}
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]" />
    </div>
  );
}
